// src/components/formBuilder/Canvas.jsx
import React from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useDrag, useDrop } from "react-dnd";

const DND_TYPE = "FORM_FIELD";

function groupChildren(fields, containerId, col) {
  return (fields || [])
    .filter((f) => f.groupId === containerId && (col == null ? true : f.groupCol === col))
    .sort((a, b) => (a.groupOrder || 0) - (b.groupOrder || 0));
}

function RowDropZone({ rowId, col, fieldsById, onMoveHere, readOnly, children }) {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DND_TYPE,
      canDrop: (item) => {
        if (readOnly) return false;
        const f = fieldsById[item.id];
        if (!f) return false;
        // Don't allow dropping structural items into a row column
        return !["row", "column", "section", "divider"].includes(f.type);
      },
      drop: (item) => {
        onMoveHere(item.id, rowId, col);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [rowId, col, fieldsById, onMoveHere, readOnly]
  );

  return (
    <Paper
      ref={drop}
      variant="outlined"
      sx={{
        p: 1.25,
        borderStyle: "dashed",
        borderColor: isOver && canDrop ? "primary.main" : "divider",
        bgcolor: isOver && canDrop ? "action.hover" : "transparent",
        transition: "background .12s ease, border-color .12s ease",
        minHeight: 84,
      }}
    >
      {children}
    </Paper>
  );
}

/**
 * Draggable shell for a field/section/divider/row container on the main canvas grid
 */
function DraggableField({
  f,
  index,
  selectedFieldId,
  onDuplicate,
  onRemove,
  onUpdateField,
  setSelectedFieldId,
  moveItem,
  readOnly = false,
  isContainer = false,
}) {
  const ref = React.useRef(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type: DND_TYPE,
    item: { id: f.id, index },
    canDrag: !readOnly,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [, drop] = useDrop({
    accept: DND_TYPE,
    hover(item, monitor) {
      if (!ref.current) return;
      if (readOnly) return;

      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drop(preview(ref));

  const selected = f.id === selectedFieldId;
  const colSpan = f?.layout?.colSpan === 2 ? 2 : 1;

  const isSection = f.type === "section";
  const isDivider = f.type === "divider";
  const isRow = f.type === "row";
  const isColumn = f.type === "column";

  return (
    <Paper
      ref={ref}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedFieldId(f.id);
      }}
      variant={selected ? "outlined" : "elevation"}
      sx={{
        p: 1.5,
        borderColor: selected ? "primary.main" : "divider",
        gridColumn: isSection || isDivider || isRow || isColumn ? "1 / -1" : `span ${colSpan}`,
        cursor: "pointer",
        opacity: isDragging ? 0.6 : 1,
        transition: "opacity .15s ease",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={readOnly ? "Published templates are read-only" : "Drag to reorder"}>
            <span>
              <IconButton
                size="small"
                onMouseDown={(e) => e.stopPropagation()}
                ref={drag}
                disabled={readOnly}
                sx={{ cursor: readOnly ? "not-allowed" : "grab" }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Typography variant="subtitle2">
            {isSection
              ? "Section"
              : isDivider
              ? "Divider"
              : isRow
              ? "Row Layout"
              : isColumn
              ? "Column Layout"
              : f.label || f.name}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {!isContainer && !isSection && !isDivider && (
            <ToggleButtonGroup
              size="small"
              value={colSpan}
              exclusive
              onChange={(_, val) => {
                if (!val || readOnly) return;
                onUpdateField(f.id, { layout: { ...(f.layout || {}), colSpan: val } });
              }}
            >
              <ToggleButton value={1}>1 col</ToggleButton>
              <ToggleButton value={2}>2 col</ToggleButton>
            </ToggleButtonGroup>
          )}

          {!readOnly && (
            <>
              <Tooltip title="Duplicate">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate(f.id);
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(f.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>

      {isSection ? (
        <>
          <Typography variant="subtitle1">{f.title || "Section"}</Typography>
          {f.subtitle && (
            <Typography variant="body2" color="text.secondary">
              {f.subtitle}
            </Typography>
          )}
          <Divider sx={{ mt: 1 }} />
        </>
      ) : isDivider ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {f.label || "Divider"}
          </Typography>
          <Divider />
        </>
      ) : isRow ? (
        <Typography variant="caption" color="text.secondary">
          Drag fields into Column A / B below.
        </Typography>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">
            Type: {f.type} {f.required ? "• required" : ""}
          </Typography>
          {f.helperText && (
            <Typography variant="caption" sx={{ display: "block" }}>
              Hint: {f.helperText}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}

function GroupedFieldCard({ f, readOnly, setSelectedFieldId, onUpdateField }) {
  const [{ isDragging }, drag] = useDrag({
    type: DND_TYPE,
    item: { id: f.id },
    canDrag: !readOnly,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  return (
    <Paper
      ref={drag}
      variant="outlined"
      onClick={() => setSelectedFieldId(f.id)}
      sx={{
        p: 1,
        cursor: readOnly ? "default" : "grab",
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {f.label || f.name || "Field"}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {f.type}
      </Typography>

      {!readOnly && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="text"
            onClick={(e) => {
              e.stopPropagation();
              onUpdateField(f.id, { groupId: null, groupCol: null });
            }}
          >
            Remove from row
          </Button>
        </Box>
      )}
    </Paper>
  );
}

export default function Canvas({
  schema,
  selectedFieldId,
  setSelectedFieldId,
  onAdd,
  onReorder,
  onRemove,
  onDuplicate,
  onUpdateField,
  onClear,
  readOnly = false,
}) {
  const allFields = schema.fields || [];

  const fieldsById = React.useMemo(() => {
    const map = {};
    for (const f of allFields) map[f.id] = f;
    return map;
  }, [allFields]);

  // only show non-grouped on the main grid
  const fields = React.useMemo(() => allFields.filter((f) => !f.groupId), [allFields]);

  const moveItem = React.useCallback(
    (fromIndex, toIndex) => {
      const fromId = fields[fromIndex]?.id;
      const toId = fields[toIndex]?.id;
      if (!fromId || !toId || fromId === toId) return;
      onReorder(fromId, toId);
    },
    [fields, onReorder]
  );

  const moveIntoRowCol = React.useCallback(
    (fieldId, rowId, col) => {
      if (readOnly) return;
      onUpdateField(fieldId, {
        groupId: rowId,
        groupCol: col,
        groupOrder: Date.now(),
      });
      setSelectedFieldId(fieldId);
    },
    [onUpdateField, readOnly, setSelectedFieldId]
  );

  const renderRowContainer = (row, index) => {
    const left = groupChildren(allFields, row.id, 0);
    const right = groupChildren(allFields, row.id, 1);

    return (
      <Box key={row.id} sx={{ gridColumn: "1 / -1" }}>
        <DraggableField
          f={row}
          index={index}
          selectedFieldId={selectedFieldId}
          onDuplicate={onDuplicate}
          onRemove={onRemove}
          onUpdateField={onUpdateField}
          setSelectedFieldId={setSelectedFieldId}
          moveItem={moveItem}
          readOnly={readOnly}
          isContainer
        />

        <Box
          sx={{ mt: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
          onClick={(e) => e.stopPropagation()}
        >
          <RowDropZone
            rowId={row.id}
            col={0}
            fieldsById={fieldsById}
            onMoveHere={moveIntoRowCol}
            readOnly={readOnly}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Column A (drop fields here)
            </Typography>

            <Stack spacing={1}>
              {left.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Empty
                </Typography>
              ) : (
                left.map((child) => (
                  <GroupedFieldCard
                    key={child.id}
                    f={child}
                    readOnly={readOnly}
                    setSelectedFieldId={setSelectedFieldId}
                    onUpdateField={onUpdateField}
                  />
                ))
              )}
            </Stack>
          </RowDropZone>

          <RowDropZone
            rowId={row.id}
            col={1}
            fieldsById={fieldsById}
            onMoveHere={moveIntoRowCol}
            readOnly={readOnly}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Column B (drop fields here)
            </Typography>

            <Stack spacing={1}>
              {right.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Empty
                </Typography>
              ) : (
                right.map((child) => (
                  <GroupedFieldCard
                    key={child.id}
                    f={child}
                    readOnly={readOnly}
                    setSelectedFieldId={setSelectedFieldId}
                    onUpdateField={onUpdateField}
                  />
                ))
              )}
            </Stack>
          </RowDropZone>
        </Box>
      </Box>
    );
  };

  return (
    <Box onClick={() => setSelectedFieldId(null)}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            cursor: "pointer",
            p: 1,
            m: -1,
            borderRadius: 1,
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {schema.title || "Untitled Form"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {schema.description || "Select a field, then add from the Palette to insert after it."}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Insert a section divider & heading">
            <span>
              <Button variant="outlined" onClick={() => onAdd("section")} disabled={readOnly}>
                + Section
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Clear all fields from the canvas">
            <span>
              <Button variant="outlined" color="error" onClick={onClear} disabled={readOnly}>
                Clear
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1.5,
          alignItems: "start",
          minHeight: 400,
          p: 0.5,
          pb: 6,
        }}
      >
        {fields.map((f, index) => {
          const isRow = f.type === "row";
          if (isRow) return renderRowContainer(f, index);

          const isSection = f.type === "section";
          const isDivider = f.type === "divider";
          const isContainer = f.type === "row" || f.type === "column";

          return (
            <DraggableField
              key={f.id}
              f={f}
              index={index}
              selectedFieldId={selectedFieldId}
              onDuplicate={onDuplicate}
              onRemove={onRemove}
              onUpdateField={onUpdateField}
              setSelectedFieldId={setSelectedFieldId}
              moveItem={moveItem}
              readOnly={readOnly}
              isContainer={isContainer || isSection || isDivider}
            />
          );
        })}
      </Box>
    </Box>
  );
}